import InputTextComponent from "presentation/components/Shared/InputTextComponent";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { useEffect, useRef } from "react";
import "./index.css";
import { ConfirmDialog } from "primereact/confirmdialog";
import { confirmDialog } from "primereact/confirmdialog";
import {
  useCreateParameterMutation,
  useDeleteParameterMutation,
  useParameterByFilter,
  useParametersById,
  useUpdateParameterMutation,
} from "presentation/hooks/use-query-parameter";
import { useParameterCreateStore } from "presentation/stores/use-parameter-create-store";
import { ParameterInputFilter } from "domain/models/parameter-input-filter";
import ParameterGrid from "../ParameterGrid";
import { useParams } from "react-router-dom";
import { useParameterDeleteStore } from "presentation/stores/use-parameter-delete-store";
import { translateRegexMessage } from "infra/translation/translate";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { EMAIL } from "domain/enum/email";

const ParameterSetting: React.FC = () => {
  const { email: userEmployeeEmail, userPermissions } = useAuthStore();
  const toast = useRef<Toast>(null);

  const confirmAction = (message: string, acceptCallback: () => void) => {
    confirmDialog({
      message: message,
      header: "Confirmação",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: acceptCallback,
    });
  };

  const { parameter, setParameter } = useParameterCreateStore();
  const { parameterToDelete, setParameterToDelete } = useParameterDeleteStore();
  const { id } = useParams();

  const { data: existentParameters, refetch: refetchExistentParameters } =
    useParameterByFilter({} as ParameterInputFilter);

  const {
    mutate: createParameterMutation,
    error: creationParameterError,
    isError: isCreationParameterError,
    isSuccess: isCreationParameterSuccess,
    data: parameterCreated,
  } = useCreateParameterMutation();

  const {
    mutate: updateParameterMutation,
    error: updatingParameterError,
    isError: isUpdatingParameterError,
    isSuccess: isUpdatingParameterSuccess,
  } = useUpdateParameterMutation();

  const {
    mutate: deleteParameterMutation,
    error: deletingParameterError,
    isError: isDeletingParameterError,
    isSuccess: isDeletingParameterSuccess,
  } = useDeleteParameterMutation();

  const { data, isError, isLoading, error } = useParametersById(id);

  useEffect(() => {
    if (data && data.data) {
      setParameter(data.data);
    } else if (isError) {
      console.error("Erro ao carregar dados do parâmetro:", error);
    }
  }, [data, isError, error]);

  const handleCreate = async () => {
    if (parameter) {
      parameter.lastModifiedBy = userEmployeeEmail ?? EMAIL;
      createParameterMutation(parameter)
    };
  };

  const handleUpdate = async () => {
    if (parameter) {
      parameter.lastModifiedBy = userEmployeeEmail ?? EMAIL;
      updateParameterMutation(parameter);
    }
  };

  useEffect(() => {
    if (parameterToDelete && parameterToDelete.id !== "")
      deleteParameterMutation(parameterToDelete);
  }, [parameterToDelete, deleteParameterMutation]);

  useEffect(() => {
    if (isCreationParameterError) {
      let message = [];
      message.push(creationParameterError.message);
      message = creationParameterError.response.data.metadata.errors;
      console.log("message", message);
      message.map((item: string) => {
        item = translateRegexMessage(item, "pt");
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: item || "Ocorreu um erro inesperado",
          life: 1500,
        });
      });
    }
  }, [creationParameterError, isCreationParameterError]);

  useEffect(() => {
    if (isUpdatingParameterError) {
      let message = [];
      message.push(updatingParameterError.message);
      message = updatingParameterError.response.data.metadata.errors;
      console.log("message", message);
      message.map((item: string) => {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: item || "Ocorreu um erro inesperado",
          life: 1500,
        });
      });
    }
  }, [updatingParameterError, isUpdatingParameterError]);

  useEffect(() => {
    if (isDeletingParameterError) {
      let message = [];
      message.push(deletingParameterError.message);
      message = deletingParameterError.response.data.metadata.errors;
      console.log("message", message);
      message.map((item: string) => {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: item || "Ocorreu um erro inesperado",
          life: 1500,
        });
      });
    }
  }, [deletingParameterError, isDeletingParameterError]);

  useEffect(() => {
    if (isCreationParameterSuccess) {
      const successMessage = "Parâmetro global criado com sucesso";
      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: successMessage,
        life: 1500,
      });

      setTimeout(() => 2000);

      setParameter({ type: "", value: "", id: "" });
      refetchExistentParameters();
    }
  }, [isCreationParameterSuccess, refetchExistentParameters, setParameter]);

  useEffect(() => {
    if (isUpdatingParameterSuccess) {
      const successMessage = "Parâmetro global atualizado com sucesso";
      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: successMessage,
        life: 1500,
      });

      setTimeout(() => 2000);

      setParameter({ type: "", value: "", id: "" });
      refetchExistentParameters();
    }
  }, [isUpdatingParameterSuccess, refetchExistentParameters, setParameter]);

  useEffect(() => {
    if (isDeletingParameterSuccess) {
      const successMessage = "Parâmetro global eliminado com sucesso";
      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: successMessage,
        life: 1500,
      });

      setTimeout(() => 2000);
      refetchExistentParameters();
      setParameterToDelete({ type: "", value: "", id: "" });
    }
  }, [
    isDeletingParameterSuccess,
    refetchExistentParameters,
    setParameterToDelete,
  ]);

  return (
    <div className="parameter-grid ">
      <div className="parameter-1-col">
        <InputTextComponent
          label="Tipo"
          fieldName="type"
          currentObject={parameter}
          getValue={parameter?.type}
          setValue={setParameter}
        />
      </div>

      <div className="parameter-1-col">
        <InputTextComponent
          label="Valor"
          fieldName="value"
          currentObject={parameter}
          getValue={parameter?.value}
          setValue={setParameter}
        />
      </div>

      <div className="parameter-1-col combine-buttons">
        {(!parameter || !parameter.id) && (
          <>
            <Button
              label={"Criar"}
              onClick={() =>
                confirmAction(
                  "Deseja criar um novo parâmetro global?",
                  handleCreate
                )
              }
              className="button-size "
              disabled={!parameter || !parameter.type || !parameter.value}
            />
          </>
        )}
        {parameter && parameter.id && (
          <>
            <Button
              label={"Atualizar"}
              onClick={() =>
                confirmAction(
                  "Deseja atualizar este parâmetro global?",
                  handleUpdate
                )
              }
              className="button-size "
              disabled={!parameter || !parameter.type || !parameter.value}
            />

            <Button
              label={"Cancelar"}
              onClick={() => setParameter({ type: "", value: "", id: "" })}
              className="button-size "
            />
          </>
        )}
      </div>

      <div className="timesheet-4-cols">
        <ParameterGrid />
      </div>

      <Toast ref={toast} position="bottom-right" />

      <ConfirmDialog />
    </div>
  );
};
export default ParameterSetting;
